import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { AuthState, Group } from '../types';
import { authService } from '../services/api';

interface AuthContextType {
  state: AuthState;
  login: (teamId: string, passcode: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction = 
  | { type: 'LOGIN_SUCCESS'; payload: { token: string; group: Group } }
  | { type: 'LOGOUT' }
  | { type: 'RESTORE_AUTH'; payload: { token: string; group: Group } };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
    case 'RESTORE_AUTH':
      return {
        token: action.payload.token,
        group: action.payload.group,
        isAuthenticated: true,
      };
    case 'LOGOUT':
      return {
        token: null,
        group: null,
        isAuthenticated: false,
      };
    default:
      return state;
  }
};

const initialState: AuthState = {
  token: null,
  group: null,
  isAuthenticated: false,
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const login = useCallback(async (teamId: string, passcode: string): Promise<void> => {
    try {
      const { token, group } = await authService.login(teamId, passcode);
      dispatch({ type: 'LOGIN_SUCCESS', payload: { token, group } });
    } catch (error) {
      throw error;
    }
  }, []);

  const logout = (): void => {
    authService.logout();
    dispatch({ type: 'LOGOUT' });
  };

  useEffect(() => {
    // Check for team redirect from huntmaster
    const redirectInfo = localStorage.getItem('huntmaster_team_redirect');
    console.log('AuthContext: Checking for team redirect:', redirectInfo);
    
    if (redirectInfo) {
      try {
        const { teamId, passcode, timestamp } = JSON.parse(redirectInfo);
        console.log('AuthContext: Found redirect info:', { teamId, passcode, age: Date.now() - timestamp });
        
        // Only use redirect if it's recent (within 15 seconds to give more time)
        if (Date.now() - timestamp < 15000) {
          console.log('AuthContext: Redirect is recent, proceeding with auto-login');
          localStorage.removeItem('huntmaster_team_redirect');
          // Mark this session as huntmaster-redirected
          sessionStorage.setItem('huntmaster_redirect_session', 'true');
          // Auto-login as the team
          console.log('AuthContext: Attempting auto-login with teamId:', teamId);
          login(teamId, passcode).catch((error) => {
            console.error('AuthContext: Auto-login failed:', error);
            // If auto-login fails, continue with normal auth check
            const { token, group } = authService.getStoredAuth();
            if (token && group) {
              dispatch({ type: 'RESTORE_AUTH', payload: { token, group } });
            }
          });
          return;
        } else {
          // Clean up expired redirect
          console.log('AuthContext: Redirect info expired, cleaning up');
          localStorage.removeItem('huntmaster_team_redirect');
        }
      } catch (error) {
        console.error('AuthContext: Error parsing redirect info:', error);
        localStorage.removeItem('huntmaster_team_redirect');
      }
    }
    
    // Normal auth restoration
    const { token, group } = authService.getStoredAuth();
    if (token && group) {
      dispatch({ type: 'RESTORE_AUTH', payload: { token, group } });
    }
  }, [login]);

  return (
    <AuthContext.Provider value={{ state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
